/*
 * Google reCAPTCHA Enterprise — shared loader + token minter (MOHO-132).
 *
 * Enterprise, not classic v3: the script is `enterprise.js` and the API hangs
 * off `grecaptcha.enterprise`, not `grecaptcha`. The two are not interchangeable
 * — classic calls against an Enterprise key fail.
 *
 * WHY THIS LOADS LAZILY. The global enquiry modal mounts a form on every page
 * (app/Tags/Forms.php), so "load it wherever a form exists" would put ~300 KB of
 * Google JS on every page load and cost the 100/100 PageSpeed baseline that
 * CLAUDE.md holds as an invariant. Instead nothing loads until a visitor
 * actually touches a form field, which a lab run never does.
 *
 * WHY THE TOKEN IS MINTED AT SUBMIT, NOT AT LOAD. Enterprise tokens expire two
 * minutes after they are minted. The tender forms take far longer than that to
 * fill in — several of them carry file uploads — so a token minted on first
 * interaction would routinely be dead by the time it was posted. Warming the
 * script early and minting late gets both halves: the visitor pays no latency
 * for the script download at submit, and the token is always fresh.
 */

/** Must match App\Support\Recaptcha\RecaptchaVerifier::ACTION — Enterprise
 *  rejects a token whose action doesn't equal the server's expectedAction. */
const ACTION = 'mh_form_submit';

let loaderPromise = null;

/**
 * Inject enterprise.js once per page and resolve when grecaptcha is ready.
 *
 * @param {string} siteKey
 * @returns {Promise<object>} the grecaptcha.enterprise namespace
 */
function loadScript(siteKey) {
    if (loaderPromise) {
        return loaderPromise;
    }

    loaderPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.onload = () => resolve(window.grecaptcha?.enterprise);
        script.onerror = () => {
            // Don't cache the rejection — a transient failure (network blip, ad
            // blocker, a corporate proxy) would otherwise disable reCAPTCHA for
            // the whole page session. Clearing the cache lets a later submit
            // retry the load.
            loaderPromise = null;
            reject(new Error('reCAPTCHA failed to load'));
        };
        document.head.appendChild(script);
    });

    return loaderPromise;
}

/**
 * Warm the script ahead of submit. Safe to call repeatedly — the load is
 * idempotent and a failure here is swallowed, because warming is an
 * optimisation and the real attempt happens at submit.
 *
 * @param {string} siteKey
 */
export function warmRecaptcha(siteKey) {
    if (!siteKey) return;

    loadScript(siteKey).catch(() => {});
}

/**
 * Mint a fresh token. Resolves to null when no site key is configured, which is
 * the disabled/fail-open path and mirrors RecaptchaVerifier::enabled() on the
 * server — the form then submits without a token and the server accepts it.
 *
 * @param {string} siteKey
 * @returns {Promise<string|null>}
 */
export async function getRecaptchaToken(siteKey) {
    if (!siteKey) {
        return null;
    }

    const enterprise = await loadScript(siteKey);

    return new Promise((resolve, reject) => {
        if (!enterprise?.ready) {
            reject(new Error('reCAPTCHA unavailable'));

            return;
        }

        enterprise.ready(() => {
            enterprise.execute(siteKey, { action: ACTION }).then(resolve, reject);
        });
    });
}
